
<template>
	<div class="competitors-menu">
		<div v-html="gear_icon" class="menu-gear" @click="openMenuSettings" />
		<div v-if="menuSettingsOpen" class="menu-settings-backdrop" @click.self="closeMenuSettings"
		style="position: fixed; inset: 0; z-index: 999;"></div>
		<div v-if="menuSettingsOpen" class="menu-settings-container">
			<div class="menu-settings-header">
				<h3 class="capitalize">{{ t('filter') }}</h3>
				<span class="pi pi-times" @click="closeMenuSettings"></span>
			</div>
			<div class="branding" v-if="selectedVisibility == 'kw'">
				<p>{{ t('visibility.include_branding') }}</p>
				<Checkbox :modelValue="showBranding" @click="handleBrandingToggle" data-cy="visib_include_checkbox"
				id="visibility_include_branding_switch_click" />
			</div>
			<div v-if="user.hasAccess('super_admin') == true && hasCompetitorFilters">
				<p class="capitalize">{{ t('competitors') }}</p>
				<div v-for="(competitor, index) in uniqueCompetitorLabels" :key="competitor.id" class="competitors"
					@click="handleCompetitorFilter(competitor.id)">
					<CustomCheckbox type="radio" :name="'competitor_radio'"
						:checked="selectedCompetitorsLabel === competitor.id"
						:data-cy="`competitor_${competitor.id}`" />
						<Tag :style="{ ...getTagStyle(competitor.color), width: 'fit-content', fontSize: '0.85rem' }">
							{{ decodeHtml(competitor.label) }}
						</Tag>
					</div>
				</div>
				<div v-if="showLabelFilter" class="label-filter">
					<MultiSelect v-model="selectedLabelIds" :options="labelOptions" optionLabel="label"
						optionValue="id" :placeholder="t('label', 2)" :filter="true" :showClear="true"
						class="label-multiselect">
						<template #value="{ value }">
							<span v-if="!value || value.length === 0">
								{{ t('label', 2) }}
							</span>
							<span v-else>
								{{ value.length }} {{ t('label', 2) }} selected
							</span>
						</template>
						<template #option="slotProps">
							<Tag :style="{ ...getTagStyle(slotProps.option.color), cursor: 'pointer' }">
								{{ slotProps.option.label }}
							</Tag>
						</template>
					</MultiSelect>
					<button
						type="button"
						class="label-clear-button"
						@click="selectedLabelIds = []"
					>
						{{ t('log_analyzer.clear_filter') }}
					</button>
				</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import he from 'he';
import { sanitizeHtml } from '@/utils/sanitizeHtml';
import Checkbox from '@/components/utilities/forms/checkbox.vue';
import CustomCheckbox from '@/components/utilities/customCheckBox.vue';
import MultiSelect from 'primevue/multiselect';
import Tag from 'primevue/tag';

import { getTagStyle } from '@/utils/componentsStyle';
import { useDashboardGeoStore } from '@/store/dashboard/dashboardGeo.js';
import { useKeywordsStore } from '@/store/visibility/keywords';
import { useAverageKeywordStore } from '@/store/visibility/averageKeyword';
import { useMarketStore } from '@/store/global/market';
import { useAuthStore } from '@/store/global/auth';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Stores
const KeywordsStore = useKeywordsStore();
const AverageKeywordStore = useAverageKeywordStore();
const MarketStore = useMarketStore();
const user = useAuthStore();

// Store refs
const { showBranding, selectedCompetitorsLabel, competitors_list } = storeToRefs(KeywordsStore);
const { selectedMarket } = storeToRefs(MarketStore);

import { useSubNavigationStore } from '@/store/global/subNavigation';

const subNavigationStore = useSubNavigationStore();
const { selectedVisibility } = storeToRefs(subNavigationStore);

const dashboardGeoStore = useDashboardGeoStore();
const { selectedLabelIds, labels } = storeToRefs(dashboardGeoStore);

const labelOptions = computed(() => labels.value || []);

defineProps({
	showLabelFilter: { type: Boolean, default: false }
});

// Local state
const menuSettingsOpen = ref(false);

// Computed properties
const gear_icon = computed(() => {
	const svgString = `<svg version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
	width="100%" height="100%" viewBox="0 0 438.529 438.529" style="enable-background:new 0 0 438.529 438.529;"
	xml:space="preserve" fill="var(--gris-700)"><g>
	<path d="M436.25,181.438c-1.529-2.002-3.524-3.193-5.995-3.571l-52.249-7.992c-2.854-9.137-6.756-18.461-11.704-27.98
		c3.422-4.758,8.559-11.466,15.41-20.129c6.851-8.661,11.703-14.987,14.561-18.986c1.523-2.094,2.279-4.281,2.279-6.567
		c0-2.663-0.66-4.755-1.998-6.28c-6.848-9.708-22.552-25.885-47.106-48.536c-2.275-1.903-4.661-2.854-7.132-2.854
		c-2.857,0-5.14,0.855-6.854,2.567l-40.539,30.549c-7.806-3.999-16.371-7.52-25.693-10.565l-7.994-52.529
		c-0.191-2.474-1.287-4.521-3.285-6.139C255.95,0.806,253.623,0,250.954,0h-63.38c-5.52,0-8.947,2.663-10.278,7.993
		c-2.475,9.513-5.236,27.214-8.28,53.1c-8.947,2.86-17.607,6.476-25.981,10.853l-39.399-30.549
		c-2.474-1.903-4.948-2.854-7.422-2.854c-4.187,0-13.179,6.804-26.979,20.413c-13.8,13.612-23.169,23.841-28.122,30.69
		c-1.714,2.474-2.568,4.664-2.568,6.567c0,2.286,0.95,4.57,2.853,6.851c12.751,15.42,22.936,28.549,30.55,39.403
		c-4.759,8.754-8.47,17.511-11.132,26.265l-53.105,7.992c-2.093,0.382-3.9,1.621-5.424,3.715C0.76,182.531,0,184.722,0,187.002
		v63.383c0,2.478,0.76,4.709,2.284,6.708c1.524,1.998,3.521,3.195,5.996,3.572l52.25,7.71c2.663,9.325,6.564,18.743,11.704,28.257
		c-3.424,4.761-8.563,11.468-15.415,20.129c-6.851,8.665-11.709,14.989-14.561,18.986c-1.525,2.102-2.285,4.285-2.285,6.57
		c0,2.471,0.666,4.658,1.997,6.561c7.423,10.284,23.125,26.272,47.109,47.969c2.095,2.094,4.475,3.138,7.137,3.138
		c2.857,0,5.236-0.852,7.138-2.563l40.259-30.553c7.808,3.997,16.371,7.519,25.697,10.568l7.993,52.529
		c0.193,2.471,1.287,4.518,3.283,6.14c1.997,1.622,4.331,2.423,6.995,2.423h63.38c5.53,0,8.952-2.662,10.287-7.994
		c2.471-9.514,5.229-27.213,8.274-53.098c8.946-2.858,17.607-6.476,25.981-10.855l39.402,30.84c2.663,1.712,5.141,2.563,7.42,2.563
		c4.186,0,13.131-6.752,26.833-20.27c13.709-13.511,23.13-23.79,28.264-30.837c1.711-1.902,2.569-4.09,2.569-6.561
		c0-2.478-0.947-4.862-2.857-7.139c-13.698-16.754-23.883-29.882-30.546-39.402c3.806-7.043,7.519-15.701,11.136-25.98l52.817-7.988
		c2.279-0.383,4.189-1.622,5.708-3.716c1.523-2.098,2.279-4.288,2.279-6.571v-63.376
		C438.533,185.671,437.777,183.438,436.25,181.438z M270.946,270.939c-14.271,14.277-31.497,21.416-51.676,21.416
		c-20.177,0-37.401-7.139-51.678-21.416c-14.272-14.271-21.411-31.498-21.411-51.673c0-20.177,7.135-37.401,21.411-51.678
		c14.277-14.272,31.504-21.411,51.678-21.411c20.179,0,37.406,7.139,51.676,21.411c14.274,14.277,21.413,31.501,21.413,51.678
		C292.359,239.441,285.221,256.669,270.946,270.939z"/></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g></svg>
`;
	
	return sanitizeHtml(svgString, {
		ALLOWED_TAGS: ['svg', 'g', 'path'],
		ALLOWED_ATTR: ['version', 'id', 'xmlns', 'xmlns:xlink', 'x', 'y', 'width', 'height', 'viewBox', 'style', 'xml:space', 'fill', 'd'],
		ADD_ATTR: ['fill']
	});
});

const parseCompetitorLabel = competitor => {
	if (!competitor?.label) {
		return null;
	}

	if (typeof competitor.label === 'string') {
		try {
			return JSON.parse(competitor.label);
		} catch (error) {
			return null;
		}
	}

	if (typeof competitor.label === 'object') {
		return competitor.label;
	}

	return null;
};

const uniqueCompetitorLabels = computed(() => {
	const uniqueLabels = [];
	const labelSet = new Set();

	competitors_list.value.forEach(competitor => {
		const labelObj = parseCompetitorLabel(competitor);
		if (
			labelObj &&
			labelObj.id != null &&
			typeof labelObj.label === 'string' &&
			labelObj.label.trim() !== '' &&
			!labelSet.has(labelObj.id)
		) {
			labelSet.add(labelObj.id);
			uniqueLabels.push({
				id: labelObj.id,
				label: labelObj.label,
				color: labelObj.color || '#63625f',
				description: labelObj.description || '',
			});
		}
	});

	const defaultOption = { id: null, label: t('default'), color: '#63625f' };
	uniqueLabels.unshift(defaultOption);

	return uniqueLabels;
});

const hasCompetitorFilters = computed(() => uniqueCompetitorLabels.value.length > 1);

// Methods
const openMenuSettings = () => {
	if (!menuSettingsOpen.value) {
		menuSettingsOpen.value = true;
	}
	else {
		menuSettingsOpen.value = false;
	}
};

const closeMenuSettings = () => {
	menuSettingsOpen.value = false;
};

const decodeHtml = html => {
	try {
		return he.decode(html);
	} catch (error) {
		return html;
	}
};

const handleBrandingToggle = () => {
	KeywordsStore.switchBranding();
};

const handleCompetitorFilter = (competitorId) => {
	selectedCompetitorsLabel.value = competitorId;
	AverageKeywordStore.fetchData({
		market_id: selectedMarket.value,
		project_id: null,
		branding: showBranding.value,
		filter: null,
		from: null,
		to: null,
		competitor_label_id: selectedCompetitorsLabel.value
	});
	KeywordsStore.fetchDataV2(selectedMarket.value, true, selectedCompetitorsLabel.value).then(() => {
		KeywordsStore.loading = false;
	});
};

watch(
	() => selectedMarket.value,
	marketId => {
		if (marketId) {
			KeywordsStore.fetchCompetitors(marketId, true);
		}
	},
	{ immediate: true }
);
</script>

<style lang="scss" scoped>
.competitors-menu {
	position: relative;
}

.menu-gear {
	width: 25px;
	height: 25px;
	cursor: pointer;
}

.menu-settings-backdrop {
	position: fixed;
	inset: 0;
	z-index: 999;
}

.menu-settings-container {
	position: absolute;
	top: 35px;
	left: -10px;
	background-color: var(--blanc);
	border: 1px solid var(--gris-200);
	border-radius: 8px;
	padding: .8rem 1rem;
	z-index: 1000;
	display: flex;
	flex-direction: column;
	gap: 1rem;
	min-width: 200px;
	max-width: 280px;
	box-sizing: border-box;

	.menu-settings-header {
		display: flex;
		justify-content: space-between;
		width: 100%;
		margin-bottom: .5rem;

		.pi-times {
			font-size: 1rem;
			cursor: pointer;
			color: var(--gris-400);
		}
	}

	.branding {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: .8rem;
		width: 100%;
		text-wrap: nowrap;
	}

}

.competitors {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: .8rem;
	width: 100%;
	margin-top: .5rem;

	&:first-child {
		margin-top: 1rem;
	}

	&:hover {
		cursor: pointer;
	}
}

.capitalize {
	text-transform: capitalize;
}

.label-filter {
	display: flex;
	align-items: center;
	flex-direction: column;
	gap: .5rem;
}

.label-multiselect {
	min-width: 220px;
	max-width: 100%;
}

:deep(.label-multiselect .p-multiselect-label) {
	white-space: normal;
	overflow: hidden;
	display: -webkit-box;
	line-clamp: 3;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
}

.label-clear-button {
	align-self: flex-start;
	border: none;
	background: transparent;
	color: var(--bleu-500);
	font-size: .8rem;
	cursor: pointer;
	padding: 0;
}
</style>
